import { type FC, useEffect } from 'react';
import { useOrganizationId } from '@features/user';
import { Alert, Paper, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useAppointmentLabelsEnabled } from '../useAppointmentLabelsEnabled';

import { AppointmentLabelsManager } from './AppointmentLabelsManager/AppointmentLabelsManager';

/**
 * Practice settings page for managing appointment labels + groups (GDN-2019).
 * Mutations are gated server-side by `manage_appointment_labels`.
 */
export const PracticeAppointmentLabelsPage: FC = () => {
  const organizationId = useOrganizationId();
  const labelsEnabled = useAppointmentLabelsEnabled();
  const { appointmentLabels: tracking } = useEventTracking();

  useEffect(() => {
    if (labelsEnabled && organizationId) {
      tracking.catalogOpened({ source: 'practice_page' });
    }
  }, [labelsEnabled, organizationId, tracking]);

  if (!labelsEnabled) {
    return <Alert severity="info">This feature isn’t enabled for your practice.</Alert>;
  }
  if (!organizationId) {
    return <Alert severity="info">No organization selected.</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Appointment Labels
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 680 }}>
          Create labels and groups to organize your calendar into swimlanes — for
          example a “Spravato Rooms” group with “Room 1” and “Room 2”, or a
          “Discipline” group with “Psychiatry” and “Psychology”.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <AppointmentLabelsManager organizationId={organizationId} />
      </Paper>
    </Stack>
  );
};

export default PracticeAppointmentLabelsPage;
